import { FiBook } from 'react-icons/fi';

export const awlSublist810Data = {
  courseId: 'awl-sublist-8-10',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublists 8-10',
  icon: FiBook,
  overview: {
    impact: 'Master advanced academic vocabulary for Band 8+ performance',
    focus: '12 advanced words from Coxhead\'s AWL Sublists 8, 9, and 10',
    uses: 'Advanced reading, sophisticated writing',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublists 8-10 contain the least frequent AWL words, but they are powerful tools for sophisticated academic expression. Using these accurately demonstrates a truly advanced command of English and can push your IELTS score into the Band 8-9 range.</p>',
      points: [
        'These words distinguish Band 7 writers from Band 8+ writers',
        'They appear in complex IELTS Reading passages about science, philosophy, and research',
        'Even using 3-4 of these correctly in an essay makes a strong impression on examiners',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['abandon', 'To give up completely; to leave behind', 'The researchers were forced to abandon the experiment due to funding cuts.'],
          ['accompany', 'To go together with; to exist alongside', 'Economic growth is often accompanied by environmental degradation.'],
          ['accumulate', 'To gather or build up over time', 'Debt can accumulate rapidly if spending is not carefully managed.'],
          ['ambiguous', 'Open to more than one interpretation; unclear', 'The wording of the contract was deliberately ambiguous.'],
          ['append', 'To add something to the end of a document or text', 'A glossary of technical terms was appended to the report.'],
          ['arbitrary', 'Based on random choice rather than reason or system', 'The selection criteria appeared entirely arbitrary and lacked transparency.'],
          ['automate', 'To convert a process to operate by machine or computer', 'Many factories have automated their production lines to reduce costs.'],
          ['bias', 'An inclination or prejudice for or against something', 'Researchers must take steps to minimise bias in their studies.'],
          ['cease', 'To come or bring to an end; to stop', 'Hostilities ceased after both sides agreed to a truce.'],
          ['coherent', 'Logical and consistent; forming a unified whole', 'The essay must present a coherent argument supported by evidence.'],
          ['compile', 'To produce by assembling information from various sources', 'The team compiled a comprehensive database of climate records.'],
          ['conform', 'To comply with rules, standards, or conventions', 'All products must conform to international safety standards.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Advanced AWL words have nuanced word families. Using the correct form in the right grammatical slot is what separates Band 7 from Band 8+.</p>',
      points: [
        'Many of these words have negative prefixes (e.g. "incoherent", "unambiguous") useful for contrast',
        'Knowing noun forms helps you nominalise verbs — a key Band 8 writing technique',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['abandon', 'abandonment', 'abandon', 'abandoned'],
          ['accompany', 'accompaniment', 'accompany', 'accompanying'],
          ['accumulate', 'accumulation', 'accumulate', 'accumulated, cumulative'],
          ['ambiguous', 'ambiguity', '—', 'ambiguous, ambiguously, unambiguous'],
          ['append', 'appendix, appendage', 'append', 'appended'],
          ['arbitrary', 'arbitrariness', '—', 'arbitrary, arbitrarily'],
          ['automate', 'automation', 'automate', 'automated, automatic, automatically'],
          ['bias', 'bias', 'bias', 'biased, unbiased'],
          ['cease', 'cessation', 'cease', 'ceaseless, ceaselessly'],
          ['coherent', 'coherence, cohesion', '—', 'coherent, coherently, incoherent'],
          ['compile', 'compilation, compiler', 'compile', 'compiled'],
          ['conform', 'conformity, conformist', 'conform', 'conformist, non-conformist'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Advanced collocations from Sublists 8-10 elevate your writing to a truly academic register. These are the word partnerships examiners expect from top-scoring candidates.</p>',
      points: [
        'Using these collocations naturally shows examiners you read widely in English',
        'They help you avoid the repetitive phrasing common in Band 6 essays',
      ],
      examples: [
        { label: 'abandon', text: 'abandon a plan, abandon hope, abandon the attempt, abandoned building' },
        { label: 'accompany', text: 'accompanied by, accompanying document, accompany a trend, musical accompaniment' },
        { label: 'accumulate', text: 'accumulate evidence, accumulate wealth, accumulate debt, cumulative effect' },
        { label: 'ambiguous', text: 'highly ambiguous, deliberately ambiguous, ambiguous wording, moral ambiguity' },
        { label: 'arbitrary', text: 'arbitrary decision, arbitrary rule, seemingly arbitrary, arbitrary nature' },
        { label: 'automate', text: 'fully automated, automate a process, automation of, increasing automation' },
        { label: 'bias', text: 'cultural bias, confirmation bias, gender bias, biased towards, unbiased assessment' },
        { label: 'cease', text: 'cease to exist, cease operations, cease fire, without ceasing' },
        { label: 'coherent', text: 'coherent argument, coherent strategy, coherent narrative, lack coherence' },
        { label: 'compile', text: 'compile data, compile a report, compile a list, comprehensive compilation' },
        { label: 'conform', text: 'conform to standards, conform to norms, pressure to conform, social conformity' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Sublists 8-10 words are most effective in Task 2 discussion and opinion essays, especially on technology, research, and social norms.</p>',
      points: [
        'Use "cease" instead of "stop" and "accumulate" instead of "build up" for formal register',
        'Describe arguments as "coherent" or criticise them as "ambiguous" for evaluative writing',
        'Use "conform to" when discussing rules, standards, or social expectations',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Robots will take over many jobs and people will stop working in factories.' },
        { label: 'Strong sentence', text: 'Increasing automation may lead many workers to cease traditional manufacturing roles, as companies seek to accumulate cost savings.' },
        { label: 'Weak sentence', text: 'The survey questions were unclear and the results were not fair.' },
        { label: 'Strong sentence', text: 'The ambiguous wording of the survey introduced considerable bias, undermining the coherence of the findings.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "ambiguous" mean?', options: ['Very clear and direct', 'Open to more than one interpretation', 'Entirely false'], answer: 'Open to more than one interpretation', explanation: '"Ambiguous" means unclear because it can be understood in more than one way.' },
        { text: 'What does "arbitrary" mean?', options: ['Based on careful analysis', 'Based on random choice rather than reason', 'Based on majority opinion'], answer: 'Based on random choice rather than reason', explanation: '"Arbitrary" describes decisions made without a logical or fair system.' },
        { text: 'What does "coherent" mean?', options: ['Logical and consistent', 'Scattered and confusing', 'Brief and incomplete'], answer: 'Logical and consistent', explanation: '"Coherent" means well-structured and logically connected — essential for Band 7+ essays.' },
        { text: 'What does "accumulate" mean?', options: ['To lose gradually over time', 'To gather or build up over time', 'To distribute evenly'], answer: 'To gather or build up over time', explanation: '"Accumulate" means to increase gradually, whether wealth, evidence, or debt.' },
        { text: 'What does "conform" mean?', options: ['To rebel against rules', 'To comply with rules or standards', 'To transform completely'], answer: 'To comply with rules or standards', explanation: '"Conform to" means to follow or comply with established rules, norms, or expectations.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublists 8-10.',
      questions: [
        { text: 'The factory has ___ most of its assembly lines, replacing workers with robots.', answer: 'automated', explanation: '"Automated" (past participle as adjective/verb) describes the conversion of a process to machine operation.' },
        { text: 'All submitted papers must ___ to the journal\'s formatting guidelines.', answer: 'conform', explanation: '"Conform to" is the standard pattern meaning to comply with rules or standards.' },
        { text: 'The study was criticised for its ___ towards Western cultural norms.', answer: 'bias', explanation: '"Bias towards" indicates a prejudice or unfair inclination in a particular direction.' },
        { text: 'The essay lacked a ___ structure, making it difficult for the reader to follow.', answer: 'coherent', explanation: '"A coherent structure" means one that is logical and well-organised.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'The ___ of wealth in the hands of a few is a growing concern. (accumulate)', options: ['accumulation', 'accumulate', 'accumulated'], answer: 'accumulation', explanation: '"The accumulation of" requires the noun form after the article.' },
        { text: 'The instructions were ___, leading to widespread confusion. (ambiguous)', options: ['ambiguity', 'ambiguous', 'ambiguously'], answer: 'ambiguous', explanation: 'An adjective is needed after "were" to describe the instructions.' },
        { text: 'The ___ of routine tasks has freed employees for more creative work. (automate)', options: ['automated', 'automation', 'automate'], answer: 'automation', explanation: '"The automation of" requires the noun form — it is the subject of the sentence.' },
        { text: 'The decision appeared completely ___; no clear criteria were used. (arbitrary)', options: ['arbitrary', 'arbitrariness', 'arbitrarily'], answer: 'arbitrary', explanation: 'An adjective is needed after "appeared" — "arbitrary" describes the decision.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "bias" about research methodology.', example: 'To ensure the validity of the findings, researchers must implement measures to minimise confirmation bias throughout the study.', tip: 'Use "minimise bias", "confirmation bias", or "biased towards" for academic precision.' },
        { text: 'Write a sentence using "automate" about the future of work.', example: 'The increasing automation of routine tasks raises important questions about the future of employment in developing economies.', tip: 'Use the noun form "automation" for a nominalised, academic style.' },
        { text: 'Write a sentence using "coherent" about academic writing.', example: 'A well-structured essay presents a coherent argument that guides the reader logically from introduction to conclusion.', tip: 'Pair with "argument", "strategy", "narrative", or "structure" for strong collocations.' },
      ],
    },
  ],
  tips: [
    '"Bias" is relevant to nearly every IELTS topic involving research, media, or policy — learn its collocations well',
    'Use "coherent" to describe strong arguments and "ambiguous" to critique weak ones — perfect for evaluation essays',
    '"Conform to" is ideal for essays about social norms, rules, and standardisation',
    'Nominalise verbs for a more academic style: "automate" → "automation", "accumulate" → "accumulation"',
  ],
  dailyPractice: [
    'Write a paragraph about AI using "automate", "cease", and "accumulate"',
    'Read a research article abstract and identify any Sublists 8-10 vocabulary',
    'Practise using "ambiguous" and "coherent" to evaluate arguments in sample essays',
    'Create your own sentences with "conform", "compile", and "bias" in different contexts',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublists 8-10 vocabulary course covering 12 advanced academic words: abandon, accompany, accumulate, ambiguous, append, arbitrary, automate, bias, cease, coherent, compile, conform. Includes word meanings, word families, collocations, and IELTS Writing integration.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist810Data,
      title: awlSublist810Data.title + ' (Foundations)',
      overview: { ...awlSublist810Data.overview, duration: '15-20 minutes' },
      sections: awlSublist810Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist810Data.sections.length / 2))),
      exercises: awlSublist810Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist810Data,
      title: awlSublist810Data.title + ' (Advanced)',
      overview: { ...awlSublist810Data.overview, duration: '30-35 minutes' },
      sections: awlSublist810Data.sections,
      exercises: awlSublist810Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist810Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist810Data;
}
